// Pure timeline/window-selection logic (docs/04 §Playback, docs/03 §manifest).
//
// The player is deliberately dumb: the server pre-resolves the next 24h into
// consecutive, non-overlapping windows expressed in absolute unix-ms. The player
// only has to answer "which window contains `now`?" — there are no schedule
// rules, timezones, or recurrence to evaluate here. Because windows are absolute
// timestamps, a window that visually crosses local midnight (e.g. 22:00→02:00)
// is just a normal [from, to) range and needs no special handling.
//
// Kept side-effect free so it is exhaustively unit-testable.

import type { Manifest, ManifestItem, ManifestWindow } from './types.ts';

/**
 * The window whose half-open range [from, to) contains `now`, or null if none is
 * active. Ranges are half-open so back-to-back windows (prev.to === next.from)
 * never both match. If the server ever emits overlapping windows, the one with
 * the latest `from` wins (most recently started) for deterministic behaviour.
 */
export function selectActiveWindow(manifest: Manifest, now: number): ManifestWindow | null {
  let best: ManifestWindow | null = null;
  for (const w of manifest.timeline) {
    if (now >= w.from && now < w.to) {
      if (best === null || w.from > best.from) best = w;
    }
  }
  return best;
}

/**
 * The item list the player should currently loop: the active window's items, or
 * the manifest fallback when no window is active. Returns an empty array only
 * when there is no active window AND no fallback items — the caller then shows
 * the black/logo idle screen.
 */
export function activeItems(manifest: Manifest, now: number): ManifestItem[] {
  const w = selectActiveWindow(manifest, now);
  if (w && w.items.length > 0) return w.items;
  return manifest.fallback.items;
}

/**
 * A stable identity for "which set of items are we looping" so the playback
 * engine can detect a window transition and reset its index. Uses the active
 * window id-range (or 'fallback'); independent of item order within the window.
 */
export function activeSetKey(manifest: Manifest, now: number): string {
  const w = selectActiveWindow(manifest, now);
  if (w && w.items.length > 0) return `w:${w.from}-${w.to}:${w.playlist_id}`;
  return manifest.fallback.items.length > 0 ? 'fallback' : 'idle';
}

/**
 * When (unix ms) the current active set stops being valid — i.e. the next moment
 * the engine must re-evaluate `activeItems`. This lets playback cross window
 * boundaries with NO network: it is purely a function of the cached manifest.
 * Returns Infinity when nothing further changes (only fallback ahead).
 */
export function nextBoundaryAfter(manifest: Manifest, now: number): number {
  const active = selectActiveWindow(manifest, now);
  let next = Infinity;
  if (active) {
    // Current window ends at its `to`.
    next = Math.min(next, active.to);
  }
  // The soonest window that starts strictly after now also forces a re-eval
  // (in case we're currently in fallback and a window is about to begin).
  for (const w of manifest.timeline) {
    if (w.from > now) next = Math.min(next, w.from);
  }
  return next;
}
